<script setup lang="ts">
import PerformanceSparkline from '@/components/eos/charts/PerformanceSparkline.vue'
import PerformanceTable from '@/components/eos/table/PerformanceTable.vue'

import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useORPerformanceTrackerService } from '@/composables/eos/or-performance-tracker'
import type { ORPerformanceTrackLine } from '@/types/eos/ORPerformanceTracker'

import { formatCurrency } from '@/util/formatters'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Refresh } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { sum } from 'lodash'
import { DateTime } from 'luxon'
import { computed, ref } from 'vue'

enum SummaryMode {
  Daily,
  Monthly,
}

const selectedSite = ref<ISiteIdentifier>()
const selectedMode = ref<SummaryMode>(SummaryMode.Daily)

const selectedTimezone = ref<string>('EST')
const selectedDate = ref<Date>(DateTime.now().minus({ days: 1 }).toJSDate()) // Default to yesterday

/** Flag to call the server to force-refresh the query */
const forceRefresh = ref(false)

// Query bindings

const { performanceTrackQuery, performanceTrackMonthlySummaryQuery } = useORPerformanceTrackerService()

const {
  data: dataDaily,
  error: errorDaily,
  isRefetching: isRefetchingDaily,
  refetch: refetchDaily,
  isPending: isPendingDaily,
  isLoading: isLoadingDaily,
} = performanceTrackQuery(selectedSite, selectedDate, selectedTimezone, forceRefresh)

const {
  data: dataMonthly,
  error: errorMonthly,
  isRefetching: isRefetchingMonthly,
  refetch: refetchMonthly,
  isPending: isPendingMonthly,
} = performanceTrackMonthlySummaryQuery(selectedSite, selectedDate)

// Data points and totals

/** Util: Returns MCP if the row hour participates in OR program, else 0 */
function getRowMCP(row: ORPerformanceTrackLine): number {
  return row.participating_in_program ? row.rt_gen_spin_mcp : 0
}

/** Util: Returns 1 if the row fulfills OR paticipation criteria, else 0 */
function getRowParticipationHours(row: ORPerformanceTrackLine): number {
  return row.participating_in_program ? 1 : 0
}

/** Util: Returns 1 if the hour has an affecting OR dispatch (curtailed), else 0 */
function getRowCurtailedHour(row: ORPerformanceTrackLine): number {
  return row.affecting_dispatch && row.affecting_dispatch.program_name === 'MISO Operating Reserves' ? 1 : 0
}

function getCustomerEarnings(row: ORPerformanceTrackLine): number {
  if (!row.participating_in_program) return 0
  return row.customer_earnings ?? 0
}

// MCP

/**
 * One datapoint per row, regardless whether it participates or not
 * (non-participating hours are assumed to be 0), the rationale being
 * that since it's plot data it should match scales with the others.
 */
const mcpDataPoints = computed(() => {
  if (selectedMode.value === SummaryMode.Daily && dataDaily.value) return dataDaily.value.map(getRowMCP)
  return []
})

/** Hours with participation flag set */
const hoursParticipating = computed(() => dataDaily.value?.filter((row) => row.participating_in_program) ?? [])

/** Either whatever BE says (monthly) or sum divided the amout of _participating hours_ */
const mcpAverage = computed(() => {
  if (selectedMode.value === SummaryMode.Monthly && dataMonthly.value) return dataMonthly.value.rt_gen_spin_mcp_average
  if (selectedMode.value === SummaryMode.Daily && mcpDataPoints.value) {
    if (hoursParticipating.value.length === 0) return 0
    return sum(mcpDataPoints.value) / hoursParticipating.value.length
  }
  return 0
})

// Participation hours

/** Either participating or not. Could look like [0,1,1,1,0,1,1,0,1,0,...]. */
const participationDataPoints = computed(() => {
  if (selectedMode.value === SummaryMode.Daily && dataDaily.value) return dataDaily.value.map(getRowParticipationHours)
  return []
})

const curtailedHoursDataPoints = computed(() => {
  if (selectedMode.value === SummaryMode.Daily && dataDaily.value) return dataDaily.value.map(getRowCurtailedHour)
  return []
})

/** Daily: hours with dispatch; monthly: backend total curtailed hours. */
const curtailedHoursTotals = computed(() => {
  if (selectedMode.value === SummaryMode.Monthly && dataMonthly.value) return dataMonthly.value.total_curtailed_hours
  if (selectedMode.value === SummaryMode.Daily && curtailedHoursDataPoints.value)
    return sum(curtailedHoursDataPoints.value)
  return 0
})

/** Count of participation hours in the day, otherwise monthly participating hours from BE. */
const participationTotals = computed(() => {
  if (selectedMode.value === SummaryMode.Monthly && dataMonthly.value)
    return dataMonthly.value.monthly_participating_hours
  if (selectedMode.value === SummaryMode.Daily && participationDataPoints.value)
    return sum(participationDataPoints.value)
  return 0
})

// SUM

const earningsDataPoints = computed(() => {
  if (selectedMode.value === SummaryMode.Daily && dataDaily.value)
    return dataDaily.value.map((record) => getCustomerEarnings(record))
  return []
})

const totalEarningsSum = computed(() => {
  if (selectedMode.value === SummaryMode.Monthly && dataMonthly.value) return dataMonthly.value.customer_earnings_sum
  if (selectedMode.value === SummaryMode.Daily) return sum(earningsDataPoints.value)
  return 0
})

// Query state

const dataReady = computed(() => {
  if (selectedMode.value === SummaryMode.Daily) {
    return !!dataDaily.value
  } else {
    return !!dataMonthly.value
  }
})

const anyQueryPending = computed(() => {
  if (selectedMode.value === SummaryMode.Daily) {
    return isPendingDaily.value
  } else {
    return isPendingMonthly.value
  }
})

const emptyText = computed(() => {
  const awaitingQuery =
    (!!selectedSite.value && anyQueryPending.value) || isRefetchingDaily.value || isRefetchingMonthly.value

  if (awaitingQuery) {
    return 'Loading...'
  } else if (!selectedSite.value) {
    return 'Please select a site.'
  }
  return 'No data.'
})

const shouldDisplayError = computed(() => {
  if (selectedMode.value === SummaryMode.Daily) {
    return errorDaily.value && !isRefetchingDaily.value
  } else {
    return errorMonthly.value && !isRefetchingMonthly.value
  }
})

const error = computed(() => {
  if (selectedMode.value === SummaryMode.Daily) {
    return errorDaily.value
  } else {
    return errorMonthly.value
  }
})

// Handlers

/**
 * Refetch table query with forceRefresh enabled
 */
const handleForceRefresh = async () => {
  forceRefresh.value = true
  try {
    await refetchDaily()
  } catch {
    // Error handling is done by Vue Query
  } finally {
    forceRefresh.value = false
  }
}

/** Util that disables current date and dates in the future */
const isDateDisabled = (date: Date) => {
  return DateTime.fromJSDate(date) >= DateTime.now().startOf('day')
}

const isInputDisabled = computed(() => isLoadingDaily.value || isRefetchingDaily.value)
</script>

<template>
  <ViewLayout>
    <template v-if="!error" #controls>
      <el-button
        :icon="Refresh"
        type="primary"
        class="actions-button"
        :loading="isRefetchingDaily || isRefetchingMonthly"
        :disabled="!selectedSite || !selectedDate || isRefetchingDaily"
        @click="handleForceRefresh"
        >Force Refresh</el-button
      >
    </template>
    <rl-error-result v-if="shouldDisplayError" :error="error">
      <el-button @click="() => (selectedMode === SummaryMode.Daily ? refetchDaily() : refetchMonthly())">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="flex gap-4">
        <rc-site-select v-model="selectedSite" class="w-[180px]" :disabled="isInputDisabled" />
        <el-date-picker
          v-model="selectedDate"
          class="!w-[140px]"
          type="date"
          placeholder="Select date"
          :disabled="isInputDisabled"
          :disabled-date="isDateDisabled"
          :clearable="false"
        />
      </div>
      <el-card class="flex flex-col gap-6">
        <!-- Card top -->
        <div class="flex items-center justify-between">
          <span class="uppercase text-xs">Earnings</span>
          <el-tabs v-model="selectedMode" class="max-w-[240px]">
            <el-tab-pane label="Daily" :name="SummaryMode.Daily" />
            <el-tab-pane label="Monthly" :name="SummaryMode.Monthly" />
          </el-tabs>
        </div>

        <!-- Numbers and sparklines -->
        <div class="flex items-center justify-between gap-8">
          <!-- MCP -->
          <div class="min-w-60 flex flex-col justify-between gap-1 pr-20">
            <span class="text-text-tertiary text-sm">Average MCP</span>
            <div class="flex gap-4">
              <span class="text-text-tertiary text-2xl w-max whitespace-nowrap"
                >$
                <span class="text-text-primary font-semibold">{{
                  dataReady ? formatCurrency(mcpAverage, 2, false) : '-'
                }}</span></span
              >
              <PerformanceSparkline :data-points="mcpDataPoints" color="#0BCD92" />
            </div>
          </div>

          <!-- Curtailed Hours -->
          <div class="min-w-60 flex flex-col justify-between gap-1 pr-20">
            <span class="text-text-tertiary text-sm">Curtailed Hours</span>
            <div class="flex gap-4">
              <span class="text-text-tertiary text-2xl w-max whitespace-nowrap"
                ><span class="text-text-primary font-semibold pr-1">{{
                  dataReady ? curtailedHoursTotals.toFixed(0) : '-'
                }}</span
                >h
              </span>
              <PerformanceSparkline :data-points="curtailedHoursDataPoints" color="#6366F1" />
            </div>
          </div>

          <!-- Participation Hours -->
          <div class="min-w-60 flex flex-col justify-between gap-1 pr-20">
            <span class="text-text-tertiary text-sm">Participation Hours</span>
            <div class="flex gap-4">
              <span class="text-text-tertiary text-2xl w-max whitespace-nowrap"
                ><span class="text-text-primary font-semibold pr-1">{{
                  dataReady ? participationTotals.toFixed(0) : '-'
                }}</span
                >h
              </span>
              <PerformanceSparkline :data-points="participationDataPoints" color="#FF770A" />
            </div>
          </div>

          <!-- Total -->
          <div class="min-w-60 flex flex-col justify-between gap-1 pr-20">
            <span class="text-text-tertiary text-sm">Total</span>
            <div class="flex gap-4">
              <span class="text-text-tertiary text-2xl w-max whitespace-nowrap"
                >$
                <span class="text-text-primary font-semibold">{{
                  dataReady ? formatCurrency(totalEarningsSum, 2, false) : '-'
                }}</span></span
              >
              <PerformanceSparkline :data-points="earningsDataPoints" color="#FFD21B" />
            </div>
          </div>
        </div>
      </el-card>

      <!-- Actual table -->
      <PerformanceTable
        v-if="dataReady"
        v-loading="isRefetchingDaily"
        mode="OR"
        :data="dataDaily || []"
        :monthly-summary="dataMonthly"
        :show-monthly-summary="true"
        :timezone="selectedTimezone"
      />

      <div v-else class="h-[50vh] flex items-center justify-center text-text-tertiary">{{ emptyText }}</div>

      <!-- Spacer -->
      <div class="w-full h-8" />
    </template>
  </ViewLayout>
</template>

<style lang="scss" scoped>
@use '@/styles/element/overrides';

:deep() {
  @include overrides.custom-el-tabs;
  @include overrides.wide-el-tabs;
  @include overrides.black-bg-select;
  @include overrides.black-bg-input;
}
</style>
